<script setup lang="ts">
// TODO:
// - Refactor this with ul/li for multi-level menus.
// - Get rid of the first:pl-0 for the "tabs" header variant

// Notes regarding accessibility:
// - Buttons have m-1 p-2 instead of p-3 to add space for the focus ring.

const {
  menuSlug = 'main',
  contentPathPrefix = undefined,
} = defineProps<{
  /**
   * The slug of the menu to fetch.
     */
  menuSlug?: string
  /**
   * The prefix to add to the paths of the menu items that link to content pages.
   * Used when nesting pages in a specific scope.
   */
  contentPathPrefix?: string
}>()

// Initialing this before the defaults didn't work
const { contentPathPrefix: defaultContentPathPrefix } = useAppConfig()

// Prefix content items, if a content paths prefix is set.
// It would probably be better to have a project-wide config or function to call for this,
// because we have the same issue on the content pages overview.
const prefixedPath = (item: ContentMenuItem) => {
  const prefix = contentPathPrefix || defaultContentPathPrefix
  if (!contentPathPrefix || item.systemPageId) {
    return item.path
  }
  return prefix + item.path
}

const { parentMenuItems, activeMainItem, getChildMenuItemsForMainItem } = useCmsMenu(menuSlug)

// Quick textcolor fix for Nijmegen
// const textColor = 'text-muted-400 hover:text-muted-500 dark:text-muted-500 dark:hover:text-muted-400'
const textColor = 'text-muted-600 hover:text-muted-500 dark:text-muted-300 dark:hover:text-muted-400'
const activeTextColor = '!border-primary-500 !text-muted-900 dark:!text-muted-100'

const { themeConfig } = useThemeConfig()
// const buttonVariants = computed(() => themeConfig.value?.header.menu.buttonVariants)
console.log(`themeConfig.value`, themeConfig.value)
const menuItemFontClass = computed(() => themeConfig.value?.header.menu.items?.fontClass)

// Temp fix, should be config on tenant
const { tenant } = useTenant()
const coloredMenuVariant = tenant.value == 'weerterland'

const borderClasses = computed(() => {
  // Used for the legacy layout to draw a border between the CMS menu and the auth menu
  // So actually we should only do this when the auth menu is visible, so when authenticated.
  if (themeConfig.value?.header.variant === 'legacy') {
    // Use CSS variable directly
    return 'border-b border-[var(--color-theme-header-border)] dark:border-muted-700'
  }
  return ''
})

const focusClass = 'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none' // focus:ring-offset-2

const route = useRoute()

const isActivePath = (item: ContentMenuItem) => {
  return prefixedPath(item) === route.path
}

const hasActiveChild = (parentItem: ContentMenuItem) => {
  return getChildMenuItemsForMainItem(parentItem).some(child => isActivePath(child))
}

// Control submenu visibility so we can close on route changes and clicks
const openMenuParentId = ref<ContentMenuItem['id'] | null>(null)

watch(() => route.fullPath, () => {
  openMenuParentId.value = null
})

const openSubmenu = (parentId: ContentMenuItem['id']) => {
  openMenuParentId.value = parentId
}

const closeSubmenu = () => {
  openMenuParentId.value = null
}

const onSubmenuFocusOut = (event: FocusEvent) => {
  const nextFocused = event.relatedTarget as HTMLElement | null
  const submenuEl = event.currentTarget as HTMLElement
  const wrapperEl = submenuEl.parentElement
  if (nextFocused && wrapperEl && wrapperEl.contains(nextFocused)) {
    return
  }
  closeSubmenu()
}

</script>

<template>
  <div>
    <div :class="borderClasses">
      <Container class="hidden justify-between md:flex" :full-width="themeConfig?.header.fullWidth">
        <nav class="flex transition-all duration-300 " aria-label="Main menu">
          <div
            v-for="(item, index) in parentMenuItems"
            :key="index"
            class="group relative mx-1"
          >
            <BaseContentButton
              v-if="coloredMenuVariant"
              :to="prefixedPath(item)"
              :variant="[ContentButtonVariant.Primary, ContentButtonVariant.Secondary, ContentButtonVariant.Tertiary][index % 3]"
              aria-haspopup="true"
              font-weight="normal"
              @mouseenter="openSubmenu(item.id)"
              @focus="openSubmenu(item.id)"
            >
              {{ item.name }}

              <IconChevronDown
                v-if="getChildMenuItemsForMainItem(item).length > 0"
                class="text-muted-400 size-3"
                aria-hidden="true"
              />
            </BaseContentButton>

            <NuxtLink
              v-else
              :to="prefixedPath(item)"
              :class="{
                '!border-primary-500 !text-muted-900 dark:!text-muted-100':
                  item == activeMainItem || hasActiveChild(item),
                [textColor]: true,
                [menuItemFontClass || '']: true,
                [focusClass]: true,
              }"
              class="texttransition-colors flex min-w-max items-center gap-1 border-b-2 border-transparent p-2 text-center duration-300"
              exact-active-class="!border-primary-500 !text-muted-900 dark:!text-muted-100 dark:!border-primary-200"
              aria-haspopup="true"
              @mouseenter="openSubmenu(item.id)"
              @focus="openSubmenu(item.id)"
            >
              <BaseText size="sm" weight="medium">
                {{ item.name }}
              </BaseText>
              <IconChevronDown
                v-if="getChildMenuItemsForMainItem(item).length > 0"
                class="text-muted-400 size-3"
                aria-hidden="true"
              />
            </NuxtLink>

            <div
              v-if="getChildMenuItemsForMainItem(item).length > 0"
              :class="[
                'border-muted-200 dark:border-muted-700 dark:bg-muted-900 absolute left-0 top-full z-50 w-60 rounded-md border bg-white p-2 shadow-lg ring-1 ring-black/5 transition-opacity duration-150',
                openMenuParentId === item.id ? 'opacity-100' : 'pointer-events-none opacity-0'
              ]"
              role="menu"
              tabindex="-1"
              @mouseleave="closeSubmenu()"
              @focusout="onSubmenuFocusOut"
            >
              <ul class="py-1">
                <li v-for="(child, cIdx) in getChildMenuItemsForMainItem(item)" :key="cIdx">
                  <NuxtLink
                    :to="prefixedPath(child)"
                    :class="{
                      [textColor]: true,
                      [focusClass]: true,
                      [activeTextColor]: isActivePath(child),
                      'bg-muted-50 dark:bg-muted-800 font-medium': isActivePath(child),
                    }"
                    class="hover:bg-muted-50 dark:hover:bg-muted-800 my-1 flex items-center gap-2 rounded-md px-3 py-2 transition-colors duration-150"
                    :exact-active-class="activeTextColor"
                    :aria-current="isActivePath(child) ? 'page' : undefined"
                    role="menuitem"
                    @focus="openSubmenu(item.id)"
                    @click="closeSubmenu()"
                  >
                    <Icon
                      name="ph:circle-fill"
                      class="text-muted-400 size-2"
                      aria-hidden="true"
                    />
                    <BaseText size="sm" weight="medium">
                      {{ child.name }}
                    </BaseText>
                  </NuxtLink>
                </li>
              </ul>
            </div>
          </div>
        </nav>
      </Container>
    </div>
  </div>
</template>
